// A library of functions for accessing the user's extended profile.

import { useLdo, useSolidAuth, useSubject, useResource, useMatchSubject } from "@ldo/solid-react";
import { SolidProfileShapeType } from "../.ldo/solidProfile.shapeTypes";
import { UserPreferencesShapeType } from "../.ldo/spoty_ldo.shapeTypes";
import { parseRdf } from "@ldo/ldo";
import { ConnectedLdoDataset } from "@ldo/connected";
import { SolidConnectedPlugin, SolidContainerUri, SolidLeaf, SolidContainer } from "@ldo/connected-solid";
import { useCallback, useEffect, useState } from "react";
import { SolidProfile } from "../.ldo/solidProfile.typings";
import { NamedNode, Quad, Term } from "@rdfjs/types";
import { rdfs, spoty } from "./ns";

// TODO maybe create a class that derives from SolidProfile,
// with *methods* for getting the name, the storages, etc...
// That class should also store the webId

// Get the profile of the logged-in in user, if it is loaded.
export function useProfile(): SolidProfile | undefined {
  const { session } = useSolidAuth();
  return useSubject(SolidProfileShapeType, session.webId);
}

// Get the best available name for the given profile.
export function getName(profile: SolidProfile): string {
  return profile.fn || profile.name || profile['@id'] as string
}

// Get the list of storages (pods) declared in the given profile.
export function getStorageUris(profile: SolidProfile): SolidContainerUri[] {
  if (!profile.storage) { return []; }
  return profile.storage
    .map(i => i["@id"])
    .map(ensureContainerUri)
  ;
}

// Get the best available name for the logged-in user.
export function useProfileName(): string | undefined {
  const profile = useProfile();
  if (profile === undefined) { return undefined }
  return profile.fn || profile.name || profile['@id'] as string
}

// Load profile from webId, recursively following rdfs:seeAlso links,
// and invoking `finalizeLogin` when the consolidated profile is loaded.
// This will actually only happen if `loginInProgress` is true.
//
// Parameters:
// * `webId` is the webId to load the profile for
// * `guard`, if set to false, will prevent the effect from running
//   (this is useful since hooks can not be called conditionnally)
// * `finalize`, if set, will be called once the profile is entirely loaded
//
// NB: internally, this function does not use LDO's `getResource` (nor `useResource`)
// because it has a number of limitations that cause issues with webIds:
// * it does not seem to follow redirections
//   (such as https://id.inrupt.com/p20n -> https://id.inrupt.com/p20n?lookout)
// * it does not do proper content-negotiation,
//   which is a problem with webIds returning HTML by default
export function useEffectLoadFullProfile(
  webId: string | undefined,
  guard?: boolean,
  finalize?: () => void,
) {
  const { fetch } = useSolidAuth();
  const { dataset } = useLdo();

  const loadProfilePart = useCallback(async (uri: string) => {
    console.debug("loadProfilePart: start:", uri);
    if (hasNamedGraph(dataset, uri)) {
      console.debug("loadProfilePart: already loaded:", uri);
      return;
    }

    let resp;
    try {
      resp = await fetch(uri, {
        headers: { accept: "text/turtle"}
      });
    }
    catch (e) {
      console.log("ERROR fetching", webId, e);
      return;
    }
    if (resp.status >= 400) {
      console.log("ERROR fetching", webId, resp?.statusText);
      return;
    }

    const resourceTurtle = await resp.text();
    let resourceDataset;
    console.debug("loadProfilePart: got turtle for", uri)
    try {
      resourceDataset = await parseRdf(resourceTurtle, {
        baseIRI: uri,
      });
    }
    catch (e) {
      console.log("ERROR parsing", webId, e);
      return;
    }

    // recursively follow rdfs:seeAlso
    const s = { termType: "NamedNode", value: uri };
    const p = { termType: "NamedNode", value: rdfs.seeAlso };
    const seeAlsoPromises = Array
      .from(resourceDataset.match(s as Term, p as Term))
      .map(q => loadProfilePart(q.object.value));

    // include loaded resource in LDO dataset
    dataset.addAll(resourceDataset.map(graphChanger(uri)));

    await Promise.all(seeAlsoPromises);
    // console.debug("loadProfilePart: exit:", uri);
  }, [dataset, fetch, webId]);

  if (guard === undefined) { guard = true; }
  useEffect(() => {
    console.debug("useEffectLoadProfile");
    if (guard && webId) {
      loadProfilePart(webId).then(finalize);
    }
  }, [guard, webId, loadProfilePart, finalize]);
}

// To avoid "never" type for language property
interface AppPreferences {
  language: string;
  localeSuffix?: string;
}

// function to load the user preferences from the pod
export function useLoadPreferences(
  webId: string | undefined,
  finalize?: () => void,
) {
  const { createData, commitData } = useLdo();
  
  const [hasCreated, setHasCreated] = useState(false);
  const profile = useProfile();
  const preferencesUri = profile?.preferencesFile?.["@id"];
  const preferencesResource = useResource(preferencesUri);
  const [podPreferences, setPodPreferences] = useState<AppPreferences | undefined>(undefined);

  const preferences = useMatchSubject(
    UserPreferencesShapeType,
    "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
    spoty.UserPreferences,
  );

  useEffect(() => {
    if (!preferencesResource || preferencesResource.isLoading() || hasCreated || !preferencesUri) return;

    if (preferences.size === 0 && preferencesResource.type !== "InvalidIdentifierResource") {
      createPreferences(preferencesUri, preferencesResource, createData, commitData);
      setHasCreated(true);
    }
  }, [preferencesResource, preferences, preferencesUri, createData, commitData, hasCreated]);

  useEffect(() => {
    const pref = preferences.toArray().find(p => p.value);
    if (pref) {
      const parsed = JSON.parse(pref.value) as AppPreferences;
      setPodPreferences(parsed);
    }
    ;
  }, [preferences]);

  return podPreferences;
}

// function to create user preferences on the pod if they do not exist
async function createPreferences(preferencesUri: string, preferencesResource: SolidLeaf | SolidContainer, createData: Function, commitData: Function) {
  const subjectUri = preferencesUri + "#UserPreferencesSpoty";

  const preferencesFile = createData(UserPreferencesShapeType, subjectUri, preferencesResource);
  preferencesFile.value = "";
  preferencesFile.type = { "@id": spoty.UserPreferences };

  // To link the fragment to the file prefs.ttl
  const rootResource = createData(UserPreferencesShapeType, preferencesUri, preferencesResource);
  rootResource["preferencesFile"] = { "@id": subjectUri };

  await commitData(rootResource);
  await commitData(preferencesFile);
}

function ensureContainerUri(uri: string): SolidContainerUri {
  if (uri.endsWith("/")) {
    return uri as SolidContainerUri;
  } else {
    return uri+"/" as SolidContainerUri;
  }
}

function makeNamedNode(uri: string): NamedNode {
  return {
    termType: "NamedNode",
    value: uri,
    equals: (other: Term | null | undefined) => { 
      return !!other && other.termType === 'NamedNode' && other.value === uri
    },
  }
}

// produce a *function* transforming quads,
// more precisely replacing the graph component of the quad
// with the given uri.
//  
function graphChanger(uri: string): (q:Quad) => Quad {
  const gn = makeNamedNode(uri);
  return q => {
    return {
      termType: "Quad",
      value: "",
      subject: q.subject,
      predicate: q.predicate,
      object: q.object,
      graph: gn,
      equals: (t: Term | null | undefined) => {
        if (t?.termType !== "Quad") {
          return false;
        }
        const tq = t as Quad;
        return q.subject.equals(tq.subject) && q.predicate.equals(tq.predicate) && q.object.equals(tq.object) && gn.equals(tq.graph)
      },
    } as Quad;
  }
}

function hasNamedGraph(dataset: ConnectedLdoDataset<SolidConnectedPlugin[]>, uri: string): boolean {
  const gn = makeNamedNode(uri);
  return !dataset.match(null, null, null, gn)[Symbol.iterator]().next().done;
}
